import React, {useState, useEffect} from 'react';
import Icon from './Icon.tsx';
import {CheckIcon, RotateCwIcon} from 'lucide-react';
import {toast} from 'react-toastify';
import {useMutation} from 'react-query';
import ImageUpload, {PasteImageButton} from './ImageUpload';
import createThumbnail from '../../lib/createThumbnail.js';
import {Button, FormGroup, Input, Label, Spinner} from 'reactstrap';
import {ProjectDataIndex, ThumbnailTarget} from '../../api/types.ts';
import useImageCreator from '../assistant/useImageCreator.ts';
import useUpdateThumbnail from '../../api/thumbnails/useUpdateThumbnail.ts';
import {registerUnsavedWork} from '../../lib/unsavedWork.ts';
import unknownErrorToString from '../../lib/unknownErrorToString.ts';

interface ThumbnailEditorProps {
    // What the thumbnail belongs to. The id is null until it has loaded.
    target: ThumbnailTarget;
    id: string | null;
    src?: string;
    // After a new thumbnail is saved, with its URL.
    onThumbnailSaved?: (thumbnail: string) => void;
}

// Picks, previews and saves a thumbnail on its own: nothing waits for the
// page's Save button.
const ThumbnailEditor: React.FC<ThumbnailEditorProps> = ({
    target,
    id,
    src,
    onThumbnailSaved
}) => {
    const [thumbnail, setThumbnail] = useState<string | undefined>(src);
    // A picture that was chosen, pasted or made but not saved yet.
    const [pending, setPending] = useState<string>();
    const [prompt, setPrompt] = useState('');

    const updateThumbnailMutation = useMutation(useUpdateThumbnail(), {
        onError: (error: unknown) => {
            toast.error(
                'Unable to save the thumbnail. ' + unknownErrorToString(error)
            );
        }
    });

    const showSaved = (url: string) => {
        setThumbnail(url);
        setPending(undefined);
        onThumbnailSaved?.(url);
    };

    // The generated image URL arrives over SignalR once the background job
    // finishes. The job has already saved it as the thumbnail.
    const {imageCount, generateImage, dailyLimit, isGeneratingImage} =
        useImageCreator(id, showSaved);

    useEffect(() => {
        setThumbnail(src);
    }, [src]);

    useEffect(() => {
        if (!pending) return;
        return registerUnsavedWork(
            () =>
                'Your new thumbnail is not saved. Choose “Save thumbnail” to keep it.'
        );
    }, [pending]);

    const save = async () => {
        if (!pending || id === null) return;

        try {
            const result = await updateThumbnailMutation.mutateAsync({
                target,
                id,
                src: pending
            });
            if (result?.thumbnail) {
                showSaved(result.thumbnail);
                toast.success('Thumbnail saved');
            }
        } catch {
            // onError has said what failed; the picture stays so they can try again.
        }
    };

    const handleImageCreator = async () => {
        if (id === null) return;

        await generateImage(prompt, id, ProjectDataIndex.Zero, true);
        setPrompt('');
    };

    const isSaving = updateThumbnailMutation.isLoading;
    const shown = pending ?? thumbnail;

    return (
        <div className="thumbnail-editor">
            {shown && (
                <div>
                    <img src={shown} crossOrigin="anonymous" alt="Thumbnail" />
                </div>
            )}
            {pending && (
                <div className="thumbnail-editor-pending">
                    <span>Not saved yet</span>
                    <div className="d-flex flex-wrap gap-2">
                        <button
                            type="button"
                            className="btn btn-icon btn-primary"
                            onClick={save}
                            disabled={isSaving || id === null}
                        >
                            {isSaving ? (
                                <Spinner size="sm" />
                            ) : (
                                <Icon icon={CheckIcon} />
                            )}{' '}
                            Save thumbnail
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setPending(undefined)}
                            disabled={isSaving}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
            <div className="mt-3">
                <ImageUpload onImage={setPending} />
            </div>
            <div className="mt-3 d-flex flex-wrap gap-2">
                <PasteImageButton onImage={setPending} />
                <button
                    type="button"
                    className="btn btn-icon btn-outline-primary"
                    onClick={() => setPending(createThumbnail())}
                >
                    <Icon icon={RotateCwIcon} /> Make thumbnail
                </button>
            </div>
            {id !== null && (
                <div className="mt-3">
                    <FormGroup>
                        <Label for="imagePrompt">AI Image Generator</Label>
                        <Input
                            type="textarea"
                            id="imagePrompt"
                            value={prompt}
                            onChange={e => setPrompt(e.target.value)}
                            placeholder="Describe the image you want to generate..."
                        />
                    </FormGroup>
                    <div className="d-flex justify-content-between align-items-center mt-2">
                        <div>
                            <strong>Images generated today:</strong>{' '}
                            {imageCount}/
                            {dailyLimit === 2147483647
                                ? 'unlimited'
                                : dailyLimit}
                        </div>
                        {isGeneratingImage && (
                            <div className="d-flex align-items-center">
                                <Spinner
                                    type="grow"
                                    size="sm"
                                    color="primary"
                                    className="me-2"
                                />
                                <span>Generating image...</span>
                            </div>
                        )}
                        <Button
                            type="button"
                            color="primary"
                            onClick={handleImageCreator}
                            disabled={
                                imageCount >= dailyLimit ||
                                prompt.trim() === '' ||
                                isGeneratingImage
                            }
                        >
                            Generate Thumbnail
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ThumbnailEditor;
